// BRANDING & ADVISOR PROFILE (owner request: Custom Advisor Branding). Each account has its own advisor
// profile; a workspace has one agency profile. Logos, photo and signature are normalised on the device
// (trimmed, proportional, small) and appear on every client-facing document, co-branded with AVRASTRO by default.
// The preview renders the exact document template the print service uses.
import { useEffect, useMemo, useRef, useState } from 'react';
import { effectiveBrand, regionPack, saveBranding, brandingProfiles, brandingOwner, ownAdvisorProfile, usableSignature, setSignature, deleteSignature, type BrandingProfile, type BrandingInput, type CoBranding, type LicensingField, type WorkspaceView } from '@vero/domain';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Field, Modal, PageHead, Seg, useConfirm } from '../core/ui.tsx';
import { DateField } from '../core/date-field.tsx';
import { Icon } from '../core/icons.tsx';
import { documentHtml } from '../core/print.ts';
import { ACCEPT_IMAGES, ImageError, normalizeImage, type NormalizeOptions } from '../core/images.ts';
import { syncService } from '../core/sync-service.ts';

type Draft = Partial<BrandingProfile>;
type Ev = { target: HTMLInputElement };
const IMAGE_OPTS: Record<'logo' | 'photo' | 'signature', NormalizeOptions> = {
  logo: { maxWidth: 900, maxHeight: 360, trim: true },
  photo: { maxWidth: 360, maxHeight: 360, square: true },
  signature: { maxWidth: 900, maxHeight: 280, trim: true, whiteToAlpha: true },
};

/** Who may edit the agency profile: the local owner, or an administrator of a shared workspace. */
export function canEditAgency(): boolean { const s = syncService()?.snapshot(); return !s?.serverUrl || !s.role || s.role === 'admin'; }

// --- image picker --------------------------------------------------------------------------------
export function ImagePicker({ kind, value, onChange, label, testId, hint }: { kind: 'logo' | 'photo'; value: string | null | undefined; onChange: (v: string | null) => void; label: string; testId: string; hint?: string }) {
  const rt = useRuntime();
  const { t } = rt;
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const take = async (f: File | null | undefined) => {
    if (!f) return;
    setBusy(true);
    try { const r = await normalizeImage(f, IMAGE_OPTS[kind]); onChange(r.dataUrl); }
    catch (e) { rt.toast(t(`branding.image_error.${e instanceof ImageError ? e.code : 'unreadable'}`), 'error'); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  return (
    <div className="field span2">
      <span>{label}</span>
      <div className={`image-drop ${kind} ${drag ? 'drag' : ''}`} onDragOver={(e: { preventDefault(): void }) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e: { preventDefault(): void; dataTransfer: DataTransfer }) => { e.preventDefault(); setDrag(false); void take(e.dataTransfer.files?.[0]); }} data-testid={`${testId}-zone`}>
        <div className="image-slot">{value ? <img src={value} alt="" data-testid={`${testId}-img`} /> : <span className="muted small">{t(`branding.empty.${kind}`)}</span>}</div>
        <div className="row wrap" style={{ gap: 6 }}>
          <label className="btn btn-sm"><Icon name="upload" />{busy ? t('branding.processing') : value ? t('branding.replace') : t('branding.upload')}
            <input ref={input} type="file" hidden accept={ACCEPT_IMAGES} onChange={(e: Ev) => void take(e.target.files?.[0])} data-testid={testId} /></label>
          {value && <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange(null)} data-testid={`${testId}-remove`}>{t('branding.remove')}</button>}
        </div>
      </div>
      {hint && <small className="muted small">{hint}</small>}
    </div>
  );
}

/** Draw a signature with mouse, pen or finger; saved trimmed on a transparent background. */
function SignaturePad({ onDone }: { onDone: (v: string | null) => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const ref = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { const c = ref.current!; const x = c.getContext('2d')!; x.lineWidth = 2.6; x.lineCap = 'round'; x.lineJoin = 'round'; x.strokeStyle = '#141a22'; }, []);
  const pos = (e: PointerEvent) => { const r = ref.current!.getBoundingClientRect(); return [(e.clientX - r.left) * (ref.current!.width / r.width), (e.clientY - r.top) * (ref.current!.height / r.height)] as const; };
  const down = (e: PointerEvent) => { drawing.current = true; ref.current!.setPointerCapture(e.pointerId); const [x, y] = pos(e); const c = ref.current!.getContext('2d')!; c.beginPath(); c.moveTo(x, y); };
  const move = (e: PointerEvent) => { if (!drawing.current) return; const [x, y] = pos(e); const c = ref.current!.getContext('2d')!; c.lineTo(x, y); c.stroke(); setDirty(true); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = ref.current!; c.getContext('2d')!.clearRect(0, 0, c.width, c.height); setDirty(false); };
  const use = async () => {
    const blob = await new Promise<Blob | null>((ok) => ref.current!.toBlob(ok, 'image/png'));
    if (!blob) return;
    const r = await normalizeImage(Object.assign(blob, { type: 'image/png' }), IMAGE_OPTS.signature);
    onDone(r.dataUrl); rt.close();
  };
  return (
    <Modal title={t('branding.draw_title')} footer={<><button className="btn" onClick={clear}>{t('branding.clear')}</button><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" disabled={!dirty} onClick={() => void use()} data-testid="signature-use">{t('branding.use_signature')}</button></>}>
      <canvas ref={ref} width={900} height={280} className="signature-pad" onPointerDown={down as never} onPointerMove={move as never} onPointerUp={up} onPointerLeave={up} data-testid="signature-canvas" />
      <p className="tiny muted">{t('branding.draw_hint')}</p>
    </Modal>
  );
}

// --- signature image (requirement 16) ------------------------------------------------------------------------------
/** The current user's OWN signature image: upload / draw, replace and delete — each confirmed, each saved at once.
 *  Another team member's image is never shown here. An image of a handwritten signature, not an electronic signature. */
export function SignatureManager() {
  const rt = useRuntime();
  const { t } = rt;
  const confirm = useConfirm();
  const own = usableSignature(rt.view, rt.userId);
  const store = (img: string | null) => {
    if (!img) return;
    const go = () => void rt.run((v, c) => setSignature(v, rt.userId, img, c), own ? 'branding.signature_replaced' : 'branding.signature_saved');
    if (own) confirm(t('branding.signature_replace_confirm'), go); else go();
  };
  const remove = () => confirm(t('branding.signature_delete_confirm'), () => void rt.run((v, c) => deleteSignature(v, rt.userId, c), 'branding.signature_deleted'), true);
  return (
    <div className="field span2" data-testid="signature-manager">
      <span>{t('branding.signature')}</span>
      <div className={`image-drop signature`} data-testid="brand-signature-zone">
        <div className="image-slot">{own ? <img src={own} alt={t('branding.signature_alt')} data-testid="brand-signature-img" /> : <span className="muted small">{t('branding.empty.signature')}</span>}</div>
        <div className="row wrap" style={{ gap: 6 }}>
          <SignatureUpload label={own ? t('branding.replace') : t('branding.upload')} onImage={store} />
          <button type="button" className="btn btn-sm" onClick={() => rt.open(<SignaturePad onDone={store} />)} data-testid="signature-draw"><Icon name="note" />{t('branding.draw')}</button>
          {own && <button type="button" className="btn btn-sm btn-ghost" onClick={remove} data-testid="brand-signature-remove">{t('branding.delete_signature')}</button>}
        </div>
      </div>
      <small className="muted small">{t('branding.signature_hint')}</small>
      <small className="muted small" data-testid="signature-note">{t('branding.signature_note')}</small>
    </div>
  );
}
function SignatureUpload({ label, onImage }: { label: string; onImage: (v: string) => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const take = async (f: File | null | undefined) => {
    if (!f) return;
    setBusy(true);
    try { onImage((await normalizeImage(f, IMAGE_OPTS.signature)).dataUrl); }
    catch (e) { rt.toast(t(`branding.image_error.${e instanceof ImageError ? e.code : 'unreadable'}`), 'error'); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  return <label className="btn btn-sm"><Icon name="upload" />{busy ? t('branding.processing') : label}
    <input ref={input} type="file" hidden accept={ACCEPT_IMAGES} onChange={(e: Ev) => void take(e.target.files?.[0])} data-testid="brand-signature" /></label>;
}

// --- preview ---------------------------------------------------------------------------------------
const SAMPLE_DOCS = ['proposal', 'review', 'letter'] as const;
type SampleDoc = typeof SAMPLE_DOCS[number];
function sampleBody(t: Runtime['t'], kind: SampleDoc): { title: string; body: string } {
  if (kind === 'letter') return { title: t('branding.sample.letter_title'), body: `<p>${t('branding.sample.client')}</p><div class="letter">${t('branding.sample.letter_body')}</div>` };
  if (kind === 'review') return { title: t('branding.sample.review_title'), body: `<h2>${t('print.section.policies')}</h2><table><tr><th>${t('policy.product')}</th><th>${t('policy.carrier')}</th><th class="num">${t('policy.premium_annual')}</th></tr><tr><td>Life Protect Plus</td><td>Aurora Life</td><td class="num">€1,200.00</td></tr><tr><td>Hospital Plan</td><td>Meridian</td><td class="num">€780.00</td></tr></table><h2>${t('print.section.next_actions')}</h2><p>${t('branding.sample.next')}</p>` };
  return { title: t('branding.sample.proposal_title'), body: `<h2>${t('proposal.section.situation')}</h2><p>${t('branding.sample.situation')}</p><h2>${t('proposal.section.recommendations')}</h2><table><tr><th>${t('policy.product')}</th><th class="num">${t('policy.premium_annual')}</th></tr><tr><td>Income Protection</td><td class="num">€540.00</td></tr></table>` };
}
/** The live preview: the real document template, with the profile being edited. */
export function BrandingPreview({ agency, advisor }: { agency: Draft | null; advisor: Draft | null }) {
  const rt = useRuntime();
  const { t } = rt;
  const [doc, setDoc] = useState<SampleDoc>('proposal');
  // the page is laid out at true A4 width (794 CSS px) and scaled to the card, so the preview matches print
  const paper = useRef<HTMLDivElement | null>(null);
  // the document is shown from an isolated blob page (no scripts allowed in it); its URL is replaced on each change
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [scale, setScale] = useState(0.6);
  useEffect(() => {
    const el = paper.current; if (!el) return;
    const fit = () => setScale(el.clientWidth / 794);
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const html = useMemo(() => {
    // the preview carries only the current user's own signature image (requirement 16)
    const own = usableSignature(rt.view, rt.userId);
    const rows = [agency, advisor ? { ...advisor, userId: rt.userId, signature: own } : null].filter(Boolean).map((r, i) => ({ id: `draft-${i}`, createdAt: `2026-01-0${i + 1}`, updatedAt: '', version: 1, ...r })) as BrandingProfile[];
    const fake = { rows: (tbl: string) => (tbl === 'branding_profiles' ? rows : []) } as unknown as WorkspaceView;
    const brand = effectiveBrand(fake, rt.userId);
    const s = sampleBody(t, doc);
    return documentHtml({ ...rt, brand }, s.title, s.body, { signature: true });
  }, [agency, advisor, doc, rt]);
  useEffect(() => {
    const f = frame.current; if (!f) return;
    f.setAttribute('sandbox', '');
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    f.src = url;
    return () => URL.revokeObjectURL(url);
  }, [html]);
  return (
    <section className="card card-pad brand-preview" data-testid="branding-preview">
      <div className="row spread wrap" style={{ gap: 8 }}><div className="section-title" style={{ margin: 0 }}>{t('branding.preview')}</div>
        <Seg values={SAMPLE_DOCS} value={doc} onChange={setDoc} label={(x) => t(`branding.sample.${x}`)} testId="branding-preview-doc" /></div>
      <div className="paper" ref={paper}><iframe ref={frame} title={t('branding.preview')} style={{ transform: `scale(${scale})` }} data-testid="branding-preview-frame" /></div>
      <p className="tiny muted" style={{ marginBottom: 0 }}>{t('branding.preview_note')}</p>
    </section>
  );
}

// --- the form ----------------------------------------------------------------------------------------
export function useBrandingDrafts() {
  const rt = useRuntime();
  const current = brandingProfiles(rt.view, rt.userId);
  // requirement 16: the form edits only the current user's OWN advisor row. A device's unclaimed profile may seed it;
  // another team member's profile (and never their signature image) does not.
  const own = ownAdvisorProfile(rt.view, rt.userId);
  const seed = own ?? (current.advisor && brandingOwner(current.advisor) === 'local' ? (({ id: _i, userId: _u, signature: _s, ...rest }) => rest)(current.advisor) : null);
  const legacy = rt.advisor;
  const [agency, setAgency] = useState<Draft>(() => current.agency ?? { scope: 'agency', agencyName: legacy.company ?? null });
  const [advisor, setAdvisor] = useState<Draft>(() => seed ?? { scope: 'advisor', advisorName: legacy.name ?? null, phone: legacy.phone ?? null, email: legacy.email ?? null, website: legacy.website ?? null, licenceNumber: legacy.license ?? null, address: legacy.officeAddress ?? null, mapsUrl: legacy.officeMapsUrl ?? null, signOff: legacy.signature ?? null, coBranding: 'co', showSignature: true, showRegulatory: true });
  const save = async (okKey = 'branding.saved') => {
    // the signature image is managed on its own (SignatureManager), never through the profile form
    const strip = (d: Draft) => { const { id: _i, createdAt: _c, updatedAt: _u, version: _v, signature: _s, userId: _o, ...rest } = d as BrandingProfile; return rest; };
    return rt.run((v, c) => {
      const a = saveBranding(v, { ...strip(advisor), id: ownAdvisorProfile(v, rt.userId)?.id, scope: 'advisor', userId: rt.userId, actingUserId: rt.userId } as BrandingInput, c);
      if (!canEditAgency()) return a;
      const g = saveBranding(v, { ...strip(agency), id: current.agency?.id, scope: 'agency', userId: null } as BrandingInput, c);
      return { ...a, action: 'branding.save', puts: [...a.puts, ...g.puts] };
    }, okKey);
  };
  return { agency, setAgency, advisor, setAdvisor, save };
}

export function BrandingForm({ drafts, compact }: { drafts: ReturnType<typeof useBrandingDrafts>; compact?: boolean }) {
  const rt = useRuntime();
  const { t } = rt;
  const { agency, setAgency, advisor, setAdvisor } = drafts;
  const pack = regionPack(advisor.country || rt.region);
  const show = (f: LicensingField) => pack.licensing.shown.includes(f);
  const req = (f: LicensingField) => pack.licensing.required.includes(f) ? ' *' : '';
  const adv = (k: keyof BrandingProfile) => ({ value: (advisor[k] as string | null | undefined) ?? '', onChange: (e: { target: HTMLInputElement | HTMLTextAreaElement }) => setAdvisor({ ...advisor, [k]: e.target.value }) });
  const ag = (k: keyof BrandingProfile) => ({ value: (agency[k] as string | null | undefined) ?? '', onChange: (e: { target: HTMLInputElement | HTMLTextAreaElement }) => setAgency({ ...agency, [k]: e.target.value }) });
  const agencyEditable = canEditAgency();
  return (
    <div className="stack">
      <section className="card card-pad">
        <div className="section-title">{t('branding.sec.identity')}</div>
        <div className="form-grid">
          <Field label={t('branding.advisor_name') + ' *'}><input className="input" {...adv('advisorName')} data-testid="brand-name" /></Field>
          <Field label={t('branding.job_title')}><input className="input" {...adv('jobTitle')} placeholder={t('branding.job_title_ph')} /></Field>
          {!compact && <ImagePicker kind="photo" value={advisor.photo} onChange={(x) => setAdvisor({ ...advisor, photo: x })} label={t('branding.photo')} testId="brand-photo" />}
        </div>
      </section>
      <section className="card card-pad">
        <div className="section-title">{t('branding.sec.licence')}</div>
        <div className="form-grid">
          <Field label={t('branding.country')}><select className="select" value={advisor.country ?? rt.region} onChange={(e: { target: HTMLSelectElement }) => setAdvisor({ ...advisor, country: e.target.value, licenceAuthority: advisor.licenceAuthority || regionPack(e.target.value).licensing.authority })}>
            {['CY', 'GR', 'GB', 'DE', 'RO', 'AE', 'IL', 'INTL'].map((r) => <option key={r} value={r}>{t(`region.${r}`)}</option>)}</select></Field>
          {show('licenceNumber') && <Field label={t('branding.licence_number') + req('licenceNumber')}><input className="input" {...adv('licenceNumber')} data-testid="brand-licence" /></Field>}
          {show('licenceAuthority') && <Field label={t('branding.licence_authority')} hint={t('branding.authority_hint')}><input className="input" {...adv('licenceAuthority')} placeholder={pack.licensing.authority ?? ''} /></Field>}
          {show('licenceExpiry') && <Field label={t('branding.licence_expiry')} group><DateField value={adv('licenceExpiry').value} onChange={(d) => setAdvisor({ ...advisor, licenceExpiry: d })} /></Field>}
          {show('regulatoryText') && <Field label={t('branding.regulatory') + req('regulatoryText')} span2 hint={t('branding.regulatory_hint')}><textarea className="textarea" rows={2} {...adv('regulatoryText')} /></Field>}
        </div>
      </section>
      <section className="card card-pad">
        <div className="section-title">{t('branding.sec.contact')}</div>
        <div className="form-grid">
          <Field label={t('settings.phone')}><input className="input" {...adv('phone')} /></Field>
          <Field label={t('settings.email')}><input className="input" type="email" {...adv('email')} data-testid="brand-email" /></Field>
          <Field label={t('settings.website')}><input className="input" {...adv('website')} placeholder="www.example.com" /></Field>
          <Field label={t('settings.office_address')}><input className="input" {...adv('address')} /></Field>
          {!compact && <Field label={t('settings.office_maps')} span2><input className="input" {...adv('mapsUrl')} /></Field>}
        </div>
      </section>
      <section className="card card-pad">
        <div className="section-title">{t('branding.sec.logos')}</div>
        <div className="form-grid">
          <Field label={t('branding.agency_name')}><input className="input" {...ag('agencyName')} disabled={!agencyEditable} data-testid="brand-agency" /></Field>
          {show('registration') && <Field label={t('branding.registration')}><input className="input" {...ag('registration')} disabled={!agencyEditable} /></Field>}
          <ImagePicker kind="logo" value={agency.logo} onChange={(x) => agencyEditable && setAgency({ ...agency, logo: x })} label={t('branding.agency_logo')} testId="brand-agency-logo" hint={agencyEditable ? t('branding.logo_hint') : t('branding.agency_admin_only')} />
          <ImagePicker kind="logo" value={advisor.logo} onChange={(x) => setAdvisor({ ...advisor, logo: x })} label={t('branding.advisor_logo')} testId="brand-logo" hint={t('branding.advisor_logo_hint')} />
        </div>
      </section>
      <section className="card card-pad">
        <div className="section-title">{t('branding.sec.signature')}</div>
        <div className="form-grid">
          <SignatureManager />
          <Field label={t('branding.sign_off')} span2 hint={t('branding.sign_off_hint')}><textarea className="textarea" rows={2} {...adv('signOff')} /></Field>
        </div>
      </section>
      <section className="card card-pad">
        <div className="section-title">{t('branding.sec.documents')}</div>
        <Field label={t('branding.mode')} group>
          <Seg values={['co', 'advisor', 'vero'] as const} value={(advisor.coBranding ?? 'co') as CoBranding} onChange={(x) => setAdvisor({ ...advisor, coBranding: x })} label={(x) => t(`branding.mode_opt.${x}`)} testId="brand-mode" />
        </Field>
        <p className="small muted" style={{ marginTop: 6 }}>{t(`branding.mode_hint.${advisor.coBranding ?? 'co'}`)}</p>
        <div className="row wrap" style={{ gap: 14 }}>
          <label className="check"><input type="checkbox" checked={!!advisor.showPhoto} onChange={(e: Ev) => setAdvisor({ ...advisor, showPhoto: e.target.checked })} />{t('branding.show_photo')}</label>
          <label className="check"><input type="checkbox" checked={advisor.showRegulatory !== false} onChange={(e: Ev) => setAdvisor({ ...advisor, showRegulatory: e.target.checked })} />{t('branding.show_regulatory')}</label>
          <label className="check">{t('branding.accent')} <input type="color" value={advisor.accent || '#9a7427'} onChange={(e: Ev) => setAdvisor({ ...advisor, accent: e.target.value })} aria-label={t('branding.accent')} />{advisor.accent && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAdvisor({ ...advisor, accent: null })}>{t('policy.color_auto')}</button>}</label>
        </div>
      </section>
    </div>
  );
}

export function BrandingView() {
  const rt = useRuntime();
  const { t } = rt;
  const drafts = useBrandingDrafts();
  const b = rt.brand;
  return (
    <div data-testid="branding">
      <PageHead title={t('branding.title')} subtitle={t('branding.subtitle')} actions={<button className="btn btn-primary" onClick={() => void drafts.save()} data-testid="branding-save"><Icon name="check" />{t('common.save')}</button>} />
      {!b.complete && <div className="callout warn small" style={{ marginBottom: 14 }}>{t('branding.incomplete')}</div>}
      <div className="grid branding-grid">
        <BrandingForm drafts={drafts} />
        <div className="branding-side"><BrandingPreview agency={drafts.agency} advisor={drafts.advisor} /></div>
      </div>
    </div>
  );
}
